import { useEffect, useRef } from "react";
import { deepEqual } from "./deepEqual";

/**
 * A custom effect that only cares about certain dependencies (watchedDeps)
 * and ignores others (ignoredDeps). If `ignoredDeps` change, it won't
 * trigger the callback. But the callback still sees the latest values.
 *
 * By default, it does a deep-equal check on watchedDeps to avoid
 * re-triggering if an object/array "looks" the same internally.
 */
export function useSelectiveEffect(callback, watchedDeps, ignoredDeps) {
  // Store the last known "watched" dependencies
  const prevWatchedRef = useRef({});
  useEffect(() => {
    let shouldTrigger = false;
    
    // Compare new vs. old watched dependencies (deep-equal)
    for (let i = 0; i < watchedDeps.length; i++) {
      if (!deepEqual(watchedDeps[i], prevWatchedRef?.current?.[i])) {
        shouldTrigger = true;
        break;
      }
    }
    
    if (shouldTrigger) {
      callback();
      // Update our ref with the new watched dependencies
      prevWatchedRef.current = watchedDeps;
    }
    // We do not update the ref if nothing changed, to avoid re-storing
    // the same references (helps performance on large objects/arrays)

    // We still list both watchedDeps and ignoredDeps in the dependency
    // array, so that the effect re-runs whenever *any* changes are present,
    // but we only call `callback()` if "watched" dependencies are truly different.
  }, [...watchedDeps, ...ignoredDeps]);
}
